/** @file The single mutable keyboard state shared by listen, holds, contexts and bindings. */
import { detectPlatform } from '../detectPlatform';
import type { KeyTarget } from '../keyTarget';
import type { Binding, Modifiers, Platform } from '../types';

/** One registerBindings call: a table active while `context` is active. */
export interface Registration {
  context: string;
  table: Binding[];
  handlers: Record<string, () => void>;
}

/** One onHold subscription. */
export interface Hold {
  code: string;
  handlers: { down: () => void; up: () => void };
}

/** A target listen() is attached to, with how many callers hold it. */
export interface Attachment {
  refs: number;
  detach: () => void;
}

export interface KeyState {
  platform: Platform;
  when: ((name: string) => boolean) | undefined;
  held: Set<string>;
  modifiers: Modifiers;
  /** Pushed contexts, oldest first; `global` is always implied. */
  contexts: { name: string }[];
  registrations: Registration[];
  holds: Hold[];
  attachments: Map<KeyTarget, Attachment>;
}

/** Module-level state: one keyboard per page, so listen/isHeld/onHold/registerBindings all see the same data. */
export const keyState: KeyState = {
  platform: detectPlatform(globalThis.navigator),
  when: undefined,
  held: new Set(),
  modifiers: { ctrl: false, meta: false, shift: false, alt: false, mod: false },
  contexts: [],
  registrations: [],
  holds: [],
  attachments: new Map(),
};
